import React, { useRef, useState } from 'react'

import { Button, Modal, Spinner } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, number, oneOf, shape, string } from 'prop-types'

import useTracker from 'Pods/Shared/Hooks/useTracker'

import checkDownloadAvailabilityAndGetUrl from 'Utils/checkDownloadAvailabilityAndGetUrl'
import downloadLimitMessage from 'Utils/downloadLimitMessage'
import { DOWNLOAD_TYPES, downloadModelEventData, pageTypeForPath } from 'Utils/downloadTracking'
import showErrorNotification from 'Utils/showErrorNotification'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import FileRow from './Components/FileRow/FileRow'
import LinkError from './Components/LinkError/LinkError'

const ALL_FILES = 'all'
const LoadingIcon = (
  <div className="cgt-icon cgt-icon__icon cgt-icon--default cgt-icon--primary">
    <Spinner size="xsmall" classNames="cgt-spinner--inline" />
  </div>
)

function csrfToken() {
  if (typeof document === 'undefined') {
    return ''
  }

  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

function DownloadFilesModal({
  state = 'available',
  title = '',
  itemId = null,
  itemPrice = 0,
  downloadType = 'paid',
  files = EMPTY_ARRAY,
  downloadAllUrl = '',
  strings,
}) {
  const [isOpen, setIsOpen] = useState(true)
  const [pendingId, setPendingId] = useState(null)
  const [isLimitReached, setIsLimitReached] = useState(false)
  const formRef = useRef(null)
  const { trackEvent } = useTracker()

  const isBusy = pendingId !== null
  const isDisabled = isBusy || isLimitReached

  const handleClose = () => setIsOpen(false)

  const trackDownload = (fileId) => {
    trackEvent('download_model', downloadModelEventData({
      itemId,
      fileId,
      itemPrice,
      downloadType,
      pageType: pageTypeForPath(window.location.pathname),
    }))
  }

  async function isDownloadAllowed(destinationUrl) {
    try {
      const response = await checkDownloadAvailabilityAndGetUrl({ itemId, destinationUrl })

      if (response.status === 'ok') {
        return true
      }

      if (response.status === 'limit_reached') {
        setIsLimitReached(true)
        showErrorNotification(downloadLimitMessage(response))
        return false
      }
    } catch {
      // Fall through to the generic error below.
    }

    showErrorNotification(strings.genericError)
    return false
  }

  const handleDownloadFile = async (file) => {
    if (isDisabled) {
      return
    }

    setPendingId(file.id)
    const allowed = await isDownloadAllowed(file.downloadUrl)
    setPendingId(null)

    if (allowed) {
      trackDownload(file.id)
      window.location.assign(file.downloadUrl)
    }
  }

  const handleDownloadAll = async (event) => {
    event.preventDefault()

    if (isDisabled) {
      return
    }

    setPendingId(ALL_FILES)
    const allowed = await isDownloadAllowed(downloadAllUrl)

    // The archive streams as an attachment, so a native form POST keeps the page (and this
    // modal) in place; limit and size refusals redirect server-side with a flash message.
    if (!allowed) {
      setPendingId(null)
      return
    }

    trackDownload(ALL_FILES)
    formRef.current?.submit()
  }

  if (!isOpen) {
    return null
  }

  const header = (
    <h2 className="download-files-modal__title cgt-heading-100">{strings.title}</h2>
  )

  function renderFooter() {
    if (state === 'too_large') {
      return <p className="download-files-modal__too-large cgt-body-md">{strings.tooLarge}</p>
    }

    if (state !== 'available') {
      return null
    }

    return (
      <form
        ref={formRef}
        className="download-files-modal__actions"
        method="post"
        action={downloadAllUrl}
        onSubmit={handleDownloadAll}
      >
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <Button
          actionType="submit"
          size="large"
          disabled={isDisabled}
          icon={pendingId === ALL_FILES ? LoadingIcon : null}
        >
          {strings.downloadAll}
        </Button>
      </form>
    )
  }

  function renderBody() {
    if (state === 'error') {
      return (
        <LinkError
          linkError={strings.linkError}
          contactSupport={strings.contactSupport}
          chatWithUs={strings.chatWithUs}
        />
      )
    }

    if (state === 'empty' || !files.length) {
      return <p className="download-files-modal__empty cgt-body-md">{strings.empty}</p>
    }

    return (
      <>
        <div className="download-files-modal__summary">
          <h3 className="download-files-modal__item-title cgt-body-md-semibold">{title}</h3>
          <p className="download-files-modal__count cgt-caption">{strings.filesIncluded}</p>
        </div>
        <ul className="download-files-modal__list" aria-busy={isBusy}>
          {files.map((file) => (
            <FileRow
              key={file.id}
              file={file}
              label={strings.downloadFile.replace('%{name}', file.name)}
              pbrReadyLabel={strings.pbrReady}
              disabled={isDisabled}
              isPending={pendingId === file.id}
              onDownload={handleDownloadFile}
            />
          ))}
        </ul>
        {renderFooter()}
        <p className="download-files-modal__status cgt-caption" role="status">
          {isBusy ? strings.preparing : ''}
        </p>
      </>
    )
  }

  return (
    <Modal header={header} onClose={handleClose} width="large" size="medium" customClass="download-files-modal">
      {renderBody()}
    </Modal>
  )
}

DownloadFilesModal.propTypes = {
  state: oneOf(['available', 'too_large', 'empty', 'error']),
  title: string,
  itemId: number,
  itemPrice: number,
  downloadType: oneOf(DOWNLOAD_TYPES),
  files: arrayOf(shape({
    id: number.isRequired,
    name: string.isRequired,
    size: string.isRequired,
    pbrReady: bool,
    downloadUrl: string.isRequired,
  })),
  downloadAllUrl: string,
  strings: shape({
    title: string.isRequired,
    filesIncluded: string,
    downloadAll: string,
    downloadFile: string,
    tooLarge: string,
    empty: string,
    pbrReady: string,
    preparing: string,
    genericError: string,
    linkError: string,
    contactSupport: string,
    chatWithUs: string,
  }).isRequired,
}

export default DownloadFilesModal
